// ─────────────────────────────────────────────────────────────────────────
// FO-321 (batch-36, stream catalogus) — vul-wizard "Catalogus vullen".
//
// De gereedschap-catalogus (UitrustingArtikel, categorie GEREEDSCHAP) wordt in
// één beheerde actie gevuld vanuit drie bronnen, blijvend GEKOPPELD aan de
// voorraad-waarheid (VoorraadArtikel). Deze module levert de gedeelde types +
// Zod-schema's voor de dry-run (preview) en de execute (transactioneel,
// idempotent op voorraadArtikelId). Eén bron = het form kan niets sturen dat de
// API weigert (CLAUDE.md: gedeeld request-schema + inline veldfouten).
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import { UitrustingDoelgroepInputSchema } from './uitrusting-doelgroep'

// ─── Bronnen ────────────────────────────────────────────────────────────────

/** De drie vul-bronnen (FO-321 §2.2). */
export const CATALOGUS_WIZARD_BRONNEN = ['SERVICEKOFFER', 'KAR_TEMPLATE', 'VOORRAAD'] as const
export type CatalogusWizardBron = (typeof CATALOGUS_WIZARD_BRONNEN)[number]

export const CATALOGUS_WIZARD_BRON_LABELS: Record<CatalogusWizardBron, string> = {
  SERVICEKOFFER: 'Servicekoffer-template',
  KAR_TEMPLATE:  'Kar-/koffertemplates',
  VOORRAAD:      'Voorraad (gereedschap)',
}

/** Vaste herkomst-labels; de kar-/koffertemplates leveren hun eigen naam als label. */
export const HERKOMST_LABEL_SERVICEKOFFER = 'Servicekoffer'
export const HERKOMST_LABEL_VOORRAAD = 'Voorraad'

// ─── Preview (dry-run) ────────────────────────────────────────────────────────

/**
 * Status per preview-item:
 * - NIEUW: bron-artikel nog niet gekoppeld, geen merk+naam-botsing → wordt aangemaakt.
 * - BESTAAT: er is al een catalogus-item met deze voorraad-koppeling → overgeslagen
 *   (idempotent; een tweede run raakt bestaande instellingen nooit).
 * - KOPPEL_VOORSTEL: een handmatig catalogus-item met dezelfde merk+naam bestaat maar
 *   is (nog) niet gekoppeld → koppel-voorstel (bevestiging per item, geen duplicaat).
 * - NIET_KOPPELBAAR: template-regel met vrije tekst / zonder voorraad-artikel → geen
 *   catalogus-item zonder bron ("maak eerst een voorraad-artikel").
 */
export const CATALOGUS_PREVIEW_STATUS = ['NIEUW', 'BESTAAT', 'KOPPEL_VOORSTEL', 'NIET_KOPPELBAAR'] as const
export type CatalogusPreviewStatus = (typeof CATALOGUS_PREVIEW_STATUS)[number]

export const CATALOGUS_PREVIEW_STATUS_LABELS: Record<CatalogusPreviewStatus, string> = {
  NIEUW:           'Nieuw',
  BESTAAT:         'Al in de catalogus',
  KOPPEL_VOORSTEL: 'Koppel-voorstel',
  NIET_KOPPELBAAR: 'Niet koppelbaar',
}

export interface CatalogusWizardPreviewItemDTO {
  /** De voorraad-bron; null bij NIET_KOPPELBAAR (vrije-tekst-regel). */
  voorraadArtikelId: string | null
  voorraadArtikelNummer: string | null
  naam: string
  merk: string | null
  /** LAYIN_MODULE: besteld als geheel; de module-inhoud is informatief. */
  isModule: boolean
  status: CatalogusPreviewStatus
  /** Welke gekozen bronnen dit item aandragen (dedupe over bronnen). */
  bronnen: CatalogusWizardBron[]
  /** Herkomst-labels die bij uitvoeren gezet worden. */
  herkomstLabels: string[]
  /** Bij BESTAAT/KOPPEL_VOORSTEL: het bestaande catalogus-item. */
  bestaandArtikelId: string | null
  bestaandArtikelNaam: string | null
  /** Bij NIET_KOPPELBAAR: korte reden voor de gebruiker. */
  reden: string | null
}

export interface CatalogusWizardPreviewTotalenDTO {
  nieuw: number
  bestaat: number
  koppelVoorstel: number
  nietKoppelbaar: number
}

export interface CatalogusWizardPreviewDTO {
  items: CatalogusWizardPreviewItemDTO[]
  totalen: CatalogusWizardPreviewTotalenDTO
}

// ─── Schema's (dry-run + execute) ─────────────────────────────────────────────

/**
 * Bron-selectie voor dry-run en execute. `voorraadSelectie` scopet de VOORRAAD-bron
 * tot specifieke artikel-ids; leeg/weggelaten = alle actieve GEREEDSCHAP-artikelen.
 * De servicekoffer- en kar-/koffertemplate-bronnen lezen altijd hun volledige set.
 */
export const CatalogusWizardBronSelectieSchema = z.object({
  bronnen: z.array(z.enum(CATALOGUS_WIZARD_BRONNEN)).min(1, 'Kies minstens één bron'),
  voorraadSelectie: z.array(z.string().min(1)).optional(),
})
export type CatalogusWizardBronSelectieInput = z.infer<typeof CatalogusWizardBronSelectieSchema>

/**
 * Execute-input: de bron-selectie + de batch-instellingen. `koppelBevestigd` bevat
 * de voorraadArtikelId's van de KOPPEL_VOORSTEL-items die de gebruiker per item heeft
 * bevestigd (alleen die worden aan hun bron gekoppeld). De server hercomputeert de
 * preview (nooit client-data vertrouwen) en handelt idempotent op voorraadArtikelId.
 */
export const CatalogusWizardExecuteSchema = CatalogusWizardBronSelectieSchema.extend({
  koppelBevestigd: z.array(z.string().min(1)).optional(),
  // Batch-instellingen (FO-321 §2.2): bestelbaar default aan, credits default 0
  // (= gratis/op aanvraag), doelgroep default iedereen (geen doelgroep-regels).
  bestelbaar: z.boolean().optional().default(true),
  credits: z.number().int().min(0).optional().default(0),
  doelgroep: UitrustingDoelgroepInputSchema.optional(),
})
export type CatalogusWizardExecuteInput = z.infer<typeof CatalogusWizardExecuteSchema>

// ─── Execute-resultaat ────────────────────────────────────────────────────────

export interface CatalogusWizardRunResultDTO {
  runId: string
  aantalNieuw: number
  aantalGekoppeld: number
  aantalOvergeslagen: number
  aangemaakteArtikelIds: string[]
  gekoppeldeArtikelIds: string[]
}
